<template>
  <el-dialog v-model="visible" center :title="title" :width="width">
    <template v-if="modelValue.invoice">
      <div class="message-body">
        <p>
          <a
            :href="modelValue.invoice"
            target="_blank"
            class="el-button el-button--primary"
          >
            Download invoice
          </a>
        </p>
        <p>
          <b> The copy of the invoice was sent to your email </b>
        </p>
        <br />
        <strong> Please after payment contact our team via LiveChat </strong>
        <p>
          Amount will be added to your balance during business time: Monday -
          Friday 5am - 5pm UTC
        </p>
        <p>
          To review all pending invoices please click
          <router-link to="/transactions?invoices" class="link">
            here
          </router-link>
        </p>
      </div>
    </template>
    <template v-else>
      <div class="message-body">
        <el-alert
          v-if="isCryptoPayment && otherActiveRequestText"
          :title="otherActiveRequestText"
          type="warning"
          :closable="false"
          show-icon
          class="other-request"
        />
        <div v-if="isCryptoPayment" class="payment-detail">
          <span class="payment-detail__label">Address:</span>
          <span class="account">{{ modelValue.account }}</span>
          <el-tooltip content="Copy address">
            <el-button
              link
              class="copy-button"
              title="Copy address"
              @click="copyText(modelValue.account)"
            >
              <font-awesome-icon icon="copy" />
            </el-button>
          </el-tooltip>
        </div>
        <div
          v-if="isCryptoPayment && modelValue.amount"
          class="payment-detail transfer-detail"
        >
          <span class="payment-detail__label">Transfer amount:</span>
          <span class="transfer-amount">
            {{ modelValue.amount }} {{ transferCurrency }}
          </span>
          <el-tooltip content="Copy amount">
            <el-button
              link
              class="copy-button"
              title="Copy amount"
              @click="copyText(modelValue.amount)"
            >
              <font-awesome-icon icon="copy" />
            </el-button>
          </el-tooltip>
        </div>
        <p
          class="message"
          :class="{ 'message--hide-account': isCryptoPayment }"
          v-html="modelValue.message"
        />
        <p>QR Code</p>
        <QRCodeVue3
          :key="modelValue.account"
          :value="modelValue.account"
          :width="150"
          :height="150"
        />
        <p class="one-time-warning">
          Do not reuse this QR code / address. For another payment or a different amount, request a new QR code / address.
        </p>
        <p>
          Please, make a transfer within {{ remainingTime }}
        </p>
        <div v-if="vatPayer">
          <p>
            <b>Please note:</b> After the payment is done it will be issued a
            vat invoice. You can download it after the payment is made.
          </p>
        </div>
      </div>
    </template>
    <template #footer>
      <span class="dialog-footer">
        <el-button type="primary" @click="close"> OK </el-button>
      </span>
    </template>
  </el-dialog>
</template>

<script lang="ts">
import { defineComponent, computed, ref, watch } from 'vue'
import QRCodeVue3 from 'qrcode-vue3'
import moment from 'moment'
import useResize from '@/hooks/useResize'
import copy from 'copy-to-clipboard'
import { ElMessage } from 'element-plus'
import { IOtherActiveRequest } from '@/interfaces/replenishments'

export interface DialogData {
  invoice: string | null
  message: string | null
  account: string
  amount: string | number | null
  currency: string | null
  title: string
  visible: boolean
  otherActiveRequest: IOtherActiveRequest | null
}
export default defineComponent({
  name: 'MessagePopup',
  components: {
    QRCodeVue3
  },
  props: {
    modelValue: {
      type: Object,
      required: true
    },
    vatPayer: {
      type: Boolean,
      default: false
    }
  },
  setup(props, context) {
    const { isDesktop } = useResize()

    const title = computed(() => {
      const paymentTitle = isCryptoPayment.value
        ? String(props.modelValue.title || '').toUpperCase()
        : props.modelValue.title

      return props.modelValue.invoice
        ? 'Please download invoice here'
        : `Your ${paymentTitle} address:`
    })

    const width = computed(() => {
      return isDesktop.value ? '50%' : '100%'
    })

    const close = () => {
      context.emit('close')
    }

    const visible = computed(() => {
      return props.modelValue.visible
    })

    const remainingSeconds = ref(0)
    const remainingTime = computed(() => {
      const hours = Math.floor(remainingSeconds.value / 3600)
      const minutes = Math.floor((remainingSeconds.value % 3600) / 60)
      const seconds = remainingSeconds.value % 60
      return [hours, minutes, seconds]
        .map((value) => String(value).padStart(2, '0'))
        .join(':')
    })

    watch(
      visible,
      (isVisible, _previous, onCleanup) => {
        if (!isVisible || props.modelValue.invoice) return

        const request = props.modelValue.otherActiveRequest
        const createdAt = request
          ? moment(request.created_at).valueOf()
          : Date.now()
        const deadline = createdAt + 24 * 60 * 60 * 1000
        const updateRemainingTime = () => {
          remainingSeconds.value = Math.max(
            0,
            Math.ceil((deadline - Date.now()) / 1000)
          )
        }

        updateRemainingTime()
        const timer = setInterval(updateRemainingTime, 1000)
        onCleanup(() => clearInterval(timer))
      },
      { immediate: true }
    )

    const transferCurrency = computed(() => {
      return String(
        props.modelValue.title || props.modelValue.currency || ''
      ).toUpperCase()
    })

    const isCryptoPayment = computed(() => {
      return /\b(USDT|USDC|BTC|ETH)\b/i.test(props.modelValue.title || '')
    })

    const otherActiveRequestText = computed(() => {
      const request = props.modelValue.otherActiveRequest
      if (!request) return ''

      const createdAt = moment(request.created_at).format('DD.MM.YYYY HH:mm')
      return `You already have an unpaid request to this address: ${request.amount} ${request.currency}, created ${createdAt}`
    })

    const copyText = (text: string | number) => {
      copy(String(text))
      ElMessage.success('Copied!')
    }

    return {
      title,
      width,
      close,
      visible,
      remainingTime,
      transferCurrency,
      isCryptoPayment,
      otherActiveRequestText,
      copyText
    }
  }
})
</script>
<style lang="scss">
.account {
  color: var(--el-color-primary);
  font-weight: bold;
}
.amount {
  text-decoration: underline;
}
.time {
  color: #f56c6c;
}
</style>
<style lang="scss" scoped>
.message-body {
  text-align: center;
}

.other-request {
  margin-bottom: 12px;
  text-align: left;
}

.payment-detail {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  justify-content: center;
  margin-bottom: 8px;
}

.payment-detail__label {
  font-weight: 600;
}

.copy-button {
  font-size: 16px;
  min-height: auto;
  padding: 0 2px;
}

.account {
  font-size: 20px;
  line-height: 1.25;
  overflow-wrap: anywhere;
}
.transfer-detail {
  color: #f56c6c;
  font-size: 20px;
  margin-bottom: 2px;
}
.one-time-warning {
  color: #f56c6c;
  font-size: 16px;
  margin-bottom: 2px;
  font-weight: bold;
}
.transfer-amount {
  font-weight: 600;
}

.message {
  margin-top: 0;
}

.message--hide-account :deep(.account) {
  display: none;
}

.message :deep(a) {
  color: var(--el-color-primary);
  text-decoration: underline;
}

.link {
  color: var(--el-color-primary);
  text-decoration: underline;
}
:deep(.el-dialog__title) {
  font-weight: bold;
}

:deep(.el-dialog__body) {
  padding-top: 0;
}

:deep(.el-dialog__header) {
  padding-bottom: 0;
}
</style>
